import { useEffect, useMemo, useState } from 'react';
import { Box, ClickAwayListener, Collapse, List, ListItemButton, ListItemText, TextField } from '@mui/material';
import { getCountries } from '../../components/order/services';
import { getCountryName } from '../services/country.service';
import { getLanguageCode } from '../services/language.service';

export interface CountryOption {
    id: string;
    name: string;
    code: string;
}

export interface SearchableCountryFieldProps {
    id: string;
    name?: string;
    label: string;
    value: string;
    onSelect: (option: CountryOption) => void;
    onBlur?: () => void;
    onValueResolved?: (option: CountryOption) => void;
    getOptionValue?: (option: CountryOption) => string;
    getOptionLabel?: (option: CountryOption) => string;
    error?: boolean;
    helperText?: React.ReactNode;
    disabled?: boolean;
    readOnly?: boolean;
    fullWidth?: boolean;
    className?: string;
    maxVisibleWithoutSearch?: number;
    maxSearchResults?: number;
}

const LIST_ROW_HEIGHT = 44;

const defaultGetOptionValue = (option: CountryOption) => option.id;
const defaultGetOptionLabel = (option: CountryOption) => getCountryName(option.code, getLanguageCode());

export const SearchableCountryField: React.FC<SearchableCountryFieldProps> = ({
    id,
    name,
    label,
    value,
    onSelect,
    onBlur,
    onValueResolved,
    getOptionValue = defaultGetOptionValue,
    getOptionLabel = defaultGetOptionLabel,
    error,
    helperText,
    disabled,
    readOnly,
    fullWidth = true,
    className,
    maxVisibleWithoutSearch = 4,
    maxSearchResults = 5,
}) => {
    const [countries, setCountries] = useState<CountryOption[]>([]);
    const [search, setSearch] = useState<string>('');
    const [isOpen, setIsOpen] = useState<boolean>(false);

    useEffect(() => {
        getCountries().then((response) => {
            setCountries((response?.data as CountryOption[]) ?? []);
        });
    }, []);

    const isInteractive = !disabled && !readOnly;

    const selectedOption = countries.find((option) => getOptionValue(option) === value);
    const selectedLabel = selectedOption ? getOptionLabel(selectedOption) : '';

    useEffect(() => {
        if (selectedOption) {
            onValueResolved?.(selectedOption);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedOption]);

    const visibleOptions = useMemo(() => {
        const query = search.trim().toLowerCase();
        if (!query) {
            return countries.slice(0, maxVisibleWithoutSearch);
        }
        return countries
            .filter(
                (option) =>
                    getOptionLabel(option).toLowerCase().includes(query) || option.code.toLowerCase().includes(query)
            )
            .slice(0, maxSearchResults);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [countries, search, maxVisibleWithoutSearch, maxSearchResults]);

    const handleSelect = (option: CountryOption) => {
        onSelect(option);
        setSearch('');
        setIsOpen(false);
    };

    const handleClickAway = () => {
        if (isOpen) {
            setIsOpen(false);
            setSearch('');
            onBlur?.();
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            if (visibleOptions.length === 1) {
                handleSelect(visibleOptions[0]);
            }
        }
    };

    return (
        <ClickAwayListener onClickAway={handleClickAway}>
            <Box style={{ position: 'relative' }}>
                <TextField
                    InputLabelProps={{ shrink: true }}
                    fullWidth={fullWidth}
                    autoComplete="off"
                    id={id}
                    name={name}
                    label={label}
                    variant="outlined"
                    size="small"
                    value={isOpen ? search : selectedLabel}
                    onChange={(event) => {
                        setSearch(event.target.value);
                        setIsOpen(true);
                    }}
                    onFocus={() => isInteractive && setIsOpen(true)}
                    onKeyDown={handleKeyDown}
                    error={error}
                    helperText={helperText}
                    disabled={disabled}
                    InputProps={{ readOnly }}
                    className={className}
                />
                <Collapse in={isOpen && isInteractive} unmountOnExit>
                    <List
                        dense
                        sx={{
                            maxHeight: maxSearchResults * LIST_ROW_HEIGHT,
                            overflowY: 'auto',
                            border: 1,
                            borderColor: 'divider',
                            borderRadius: 1,
                            mt: 0.5,
                            bgcolor: 'background.paper',
                        }}
                    >
                        {visibleOptions.map((option) => (
                            <ListItemButton key={getOptionValue(option)} onClick={() => handleSelect(option)}>
                                <ListItemText primary={getOptionLabel(option)} />
                            </ListItemButton>
                        ))}
                    </List>
                </Collapse>
            </Box>
        </ClickAwayListener>
    );
};

export default SearchableCountryField;
